import React, { useState } from "react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Check, X, Pencil, Quote, RefreshCw } from "lucide-react";

const CONFIDENCE = { High: "text-emerald-700 border-emerald-200 bg-emerald-50", Medium: "text-amber-700 border-amber-200 bg-amber-50", Low: "text-slate-500 border-slate-300 bg-slate-100" };
const STATUS = { confirmed: "Confirmed by you", rejected: "Rejected by you", pending: "Awaiting your confirmation" };

const CapabilityCard = ({ cap, onAction, busy }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cap.name);
  const [source, setSource] = useState(cap.source_text);

  return (
    <article className={`rounded-2xl border p-4 sm:p-5 ${cap.user_status === "confirmed" ? "border-emerald-200 bg-emerald-50/70" : cap.user_status === "rejected" ? "border-slate-200 bg-white opacity-70" : "border-slate-200 bg-slate-50"} shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)]`} data-testid={`capability-card-${cap.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        {editing ? (
          <input value={name} onChange={(e) => setName(e.target.value)} className="flex-1 min-w-[12rem] rounded-lg bg-slate-50 border border-slate-300 px-3 py-2 text-base sm:text-sm text-slate-900" data-testid={`capability-name-input-${cap.id}`} />
        ) : (
          <h3 className="font-bold text-slate-900 break-words" data-testid={`capability-name-${cap.id}`}>{cap.name}</h3>
        )}
        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] ${CONFIDENCE[cap.ai_confidence] || CONFIDENCE.Low}`}>AI confidence: {cap.ai_confidence}</span>
      </div>

      <p className="mt-2 text-xs text-slate-500">{STATUS[cap.user_status] || cap.user_status}{cap.experience_years ? ` · ${cap.experience_years} yrs in your profile` : ""}</p>

      {cap.why_identified && (
        <div className="mt-3 accent-bar rounded-xl border border-slate-200 bg-white p-3 pl-4" style={{ "--accent": "#0284C7" }}>
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Why we identified this</p>
          <p className="mt-1 text-sm text-slate-700">{cap.why_identified}</p>
        </div>
      )}

      <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3.5">
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Source from your profile</p>
        {editing ? (
          <textarea value={source} onChange={(e) => setSource(e.target.value)} rows={3} className="mt-1.5 w-full rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-slate-900" data-testid={`capability-source-input-${cap.id}`} />
        ) : (
          <p className="mt-1.5 flex gap-2 text-sm text-slate-700"><Quote className="w-3.5 h-3.5 text-sky-600 shrink-0 mt-1" /><span>{cap.source_text}</span></p>
        )}
        <p className="mt-1.5 text-[11px] text-slate-400">{cap.source_reference}</p>
      </div>

      {cap.potential_value && (
        <div className="mt-3 accent-bar rounded-xl border border-slate-200 bg-white p-3 pl-4" style={{ "--accent": "#0D9488" }}>
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Potential value</p>
          <p className="mt-1 text-sm text-slate-700">{cap.potential_value}</p>
        </div>
      )}

      <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
        {[["Who may need it", cap.potential_customers, "#4F46E5"], ["Problems it solves", cap.problems_solved, "#D97706"],
          ["Possible formats", cap.delivery_formats, "#0284C7"], ["Proof a buyer may want", cap.required_proof, "#059669"]].map(([label, items, accent]) => (
          items?.length ? (
            <div key={label} className="accent-bar rounded-xl border border-slate-200 bg-white p-3 pl-4" style={{ "--accent": accent }} data-testid={`capability-block-${label.toLowerCase().replace(/[^a-z]+/g, "-")}-${cap.id}`}>
              <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
              <ul className="mt-1.5 space-y-1 text-sm text-slate-700">
                {items.map((x, i) => <li key={i} className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" /><span>{x}</span></li>)}
              </ul>
            </div>
          ) : null
        ))}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        {editing ? (
          <>
            <Button disabled={busy} onClick={() => { onAction(cap, "edit", { name, source_text: source }); setEditing(false); }} className="h-10 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`capability-save-${cap.id}`}><Check className="w-4 h-4 mr-1.5" /> Save & confirm</Button>
            <Button variant="outline" onClick={() => setEditing(false)} className="h-10 border-slate-300 text-slate-700" data-testid={`capability-cancel-${cap.id}`}>Cancel</Button>
          </>
        ) : (
          <>
            <Button disabled={busy || cap.user_status === "confirmed"} onClick={() => onAction(cap, "confirm")} className="h-10 bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50" data-testid={`capability-confirm-${cap.id}`}><Check className="w-4 h-4 mr-1.5" /> Confirm</Button>
            <Button variant="outline" onClick={() => setEditing(true)} className="h-10 border-slate-300 text-slate-700" data-testid={`capability-edit-${cap.id}`}><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>
            <Button disabled={busy || cap.user_status === "rejected"} variant="outline" onClick={() => onAction(cap, "reject")} className="h-10 border-slate-300 text-slate-500 hover:text-rose-600 col-span-2 sm:col-span-1" data-testid={`capability-reject-${cap.id}`}><X className="w-4 h-4 mr-1.5" /> Not true for me</Button>
          </>
        )}
      </div>
    </article>
  );
};

export const StepCapabilities = ({ state, patch, onNext }) => {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState({ questions: [], sufficiency: null });
  const caps = state.capabilities || [];
  const confirmed = caps.filter((c) => c.user_status === "confirmed").length;

  const extract = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/accelerator/capabilities/extract${refresh ? "?refresh=true" : ""}`);
      patch({ capabilities: data.capabilities, progress: data.progress });
      setNotes({ questions: data.missing_questions || [], sufficiency: data.data_sufficiency });
      if (!data.capabilities.length) toast.error("We couldn't find a capability we can evidence. Add more detail to your resume and retry.");
      else toast.success(data.cached ? "Your saved capabilities are ready" : `${data.capabilities.length} possible capabilities found`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
    } finally {
      setLoading(false);
    }
  };

  const act = async (cap, action, fields = {}) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/accelerator/capabilities/${cap.id}`, { action, ...fields });
      patch({ capabilities: caps.map((c) => (c.id === cap.id ? data.capability : c)), progress: data.progress });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-4" data-testid="accelerator-step-capabilities-panel">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5">
        <h2 className="text-base md:text-lg font-bold text-slate-900">Step 2 — What could you potentially monetise?</h2>
        <p className="text-sm text-slate-500 mt-1.5">These are extracted from your own profile and shown with their source. They stay hypotheses until you confirm them — only confirmed capabilities move forward.</p>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <Button onClick={() => extract(false)} disabled={loading} className="h-11 bg-sky-600 hover:bg-sky-500 text-white" data-testid="accelerator-extract-button">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading your profile…</> : caps.length ? "Reload capabilities" : "Find my capabilities"}
          </Button>
          {!!caps.length && (
            <Button onClick={() => extract(true)} disabled={loading} variant="outline" className="h-11 border-slate-300 text-slate-700" data-testid="accelerator-reextract-button"><RefreshCw className="w-4 h-4 mr-1.5" /> Re-run</Button>
          )}
        </div>
        {notes.sufficiency === "limited" && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700" data-testid="accelerator-limited-note">
            Your profile gives us limited information. Answer these to improve what we can find:
            <ul className="mt-2 space-y-1 list-disc list-inside">{notes.questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
          </div>
        )}
      </div>

      {caps.map((cap) => <CapabilityCard key={cap.id} cap={cap} onAction={act} busy={busy} />)}

      {!!caps.length && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="accelerator-capabilities-footer">
          <p className="text-sm text-slate-500" data-testid="accelerator-confirmed-count">{confirmed} capabilit{confirmed === 1 ? "y" : "ies"} confirmed by you</p>
          <Button onClick={onNext} disabled={!confirmed} className="h-11 bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50" data-testid="accelerator-to-routes">See possible income routes</Button>
        </div>
      )}
    </section>
  );
};

export default StepCapabilities;
